import React, { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import { AssetBreakdownSummary } from '../../api/profit';
import './ByAssetTab.css';

interface ByAssetTabProps {
  breakdowns: AssetBreakdownSummary[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ByAssetTab: React.FC<ByAssetTabProps> = ({ breakdowns, isLoading, error, onRetry }) => {
  // Calculate totals
  const totals = useMemo(() => {
    if (breakdowns.length === 0) return null;

    return {
      net_profit: breakdowns.reduce((sum, b) => sum + b.net_profit, 0),
      gross_profit: breakdowns.reduce((sum, b) => sum + b.gross_profit, 0),
      trade_count: breakdowns.reduce((sum, b) => sum + b.trade_count, 0),
      fees_paid: breakdowns.reduce((sum, b) => sum + b.fees_paid, 0),
      percent_of_total: 100,
      avg_profit_per_trade: 0,
    };
  }, [breakdowns]);

  // Calculate avg profit per trade for totals
  if (totals && totals.trade_count > 0) {
    totals.avg_profit_per_trade = totals.net_profit / totals.trade_count;
  }

  const formatCurrency = (value: number) => {
    const sign = value < 0 ? '-' : '';
    const absValue = Math.abs(value);
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getProfitClass = (profit: number) => {
    if (profit > 0) return 'profit-positive';
    if (profit < 0) return 'profit-negative';
    return 'profit-neutral';
  };

  // Define columns
  const columns = useMemo<ColumnDef<AssetBreakdownSummary>[]>(
    () => [
      {
        accessorKey: 'product_id',
        header: 'Asset',
        cell: (info) => <span className="asset-name">{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'net_profit',
        header: 'Net Profit',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={getProfitClass(value)}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        accessorKey: 'percent_of_total',
        header: '% of Total',
        cell: (info) => {
          const value = info.getValue() as number;
          const netProfit = info.row.original.net_profit;
          return (
            <div className="percent-container">
              <span className="percent-value">{formatPercent(value)}</span>
              <div className="percent-bar-container">
                <div 
                  className={`percent-bar ${getProfitClass(netProfit)}`}
                  style={{ width: `${Math.min(Math.abs(value), 100)}%` }}
                />
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'trade_count',
        header: 'Trades',
        cell: (info) => (info.getValue() as number).toLocaleString(),
      },
      {
        accessorKey: 'avg_profit_per_trade',
        header: 'Avg/Trade',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={getProfitClass(value)}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
    ],
    []
  );

  // Initialize table with default sorting by net_profit descending
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'net_profit', desc: true }
  ]);

  const table = useReactTable({
    data: breakdowns,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="breakdown-loading">
        <div className="breakdown-loading-spinner" />
        <span>Calculating asset breakdowns...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="breakdown-error">
        <div className="breakdown-error-title">Failed to load asset breakdown</div>
        <div className="breakdown-error-message">{error}</div>
        {onRetry && (
          <button className="breakdown-retry-button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (breakdowns.length === 0) {
    return (
      <div className="breakdown-empty">
        <div className="breakdown-empty-icon">📊</div>
        <div className="breakdown-empty-title">No trading activity</div>
        <div className="breakdown-empty-message">
          No profit data available for the selected date range.
        </div>
        <div className="breakdown-empty-suggestion">
          Try selecting a different date range with trading activity.
        </div>
      </div>
    );
  }

  return (
    <div className="by-asset-tab">
      <div className="asset-table-container">
        <table className="asset-breakdown-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={`sortable ${header.column.id !== 'product_id' ? 'numeric' : ''}`}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ cursor: 'pointer' }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: ' ▲',
                      desc: ' ▼',
                    }[header.column.getIsSorted() as string] ?? null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="breakdown-row">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cell.column.id !== 'product_id' ? 'numeric' : ''}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr className="total-row">
                <td className="asset-name"><strong>Total</strong></td>
                <td className={`numeric ${getProfitClass(totals.net_profit)}`}>
                  <strong>{formatCurrency(totals.net_profit)}</strong>
                </td>
                <td className="numeric">
                  <strong>{formatPercent(totals.percent_of_total)}</strong>
                </td>
                <td className="numeric">
                  <strong>{totals.trade_count.toLocaleString()}</strong>
                </td>
                <td className={`numeric ${getProfitClass(totals.avg_profit_per_trade)}`}>
                  <strong>{formatCurrency(totals.avg_profit_per_trade)}</strong>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default ByAssetTab;
